import {
  Loading03Icon,
  NotificationSquareIcon
} from "@hugeicons-pro/core-stroke-rounded";
import { HugeiconsIcon, IconSvgElement } from "@hugeicons/react";
import { Button, ButtonSize, ButtonVariant, Show } from "shared/components";
import { Card } from "shared/components/Card";
import { ApplicationStatus } from "shared/types";
import { getFullName } from "shared/utils/get-full-name";
import { Application } from "types/application";
import { DateTime } from "luxon";
import { useMutation } from "@tanstack/react-query";
import { applicationsApi } from "api/applications";
import { useNavigate } from "react-router-dom";

const Banner = ({
  icon,
  title,
  body,
  applicantId,
  applicationId
}: {
  icon: IconSvgElement;
  title: string;
  body: string;
  applicantId?: string;
  applicationId?: string;
}) => {
  const navigate = useNavigate();

  const markAsSeen = useMutation({
    mutationKey: ["APPLICATION"],
    mutationFn: () =>
      applicationsApi.markDocumentRequestsSeen({
        applicantId: applicantId || "",
        applicationId: applicationId || ""
      }),
    onSuccess: () => {
      navigate(
        `/applications/${applicationId}/reports?applicantId=${applicantId}&report=additional_documents`
      );
    },
    onError: () => {
      // in both examples we want to navigate user
      navigate(
        `/applications/${applicationId}/reports?applicantId=${applicantId}&report=additional_documents`
      );
    }
  });

  return (
    <Card className="h-[84px] px-6 flex gap-6 items-center animate-appear">
      <HugeiconsIcon
        size={50}
        strokeWidth={1}
        className="text-content-1000"
        icon={icon}
      />

      <div>
        <h2 className="text-base text-content-1000 font-bold mb-1">{title}</h2>
        <p className="text-sm font-medium text-content-300">{body}</p>
      </div>

      <Show if={applicantId && applicationId}>
        <Button
          size={ButtonSize.XS}
          loading={markAsSeen.isPending}
          onClick={() => markAsSeen.mutate()}
          variant={ButtonVariant.PRIMARY}
          className="ml-auto"
        >
          View documents
        </Button>
      </Show>
    </Card>
  );
};

export const UploadedDocumentsBanners = ({
  application
}: {
  application: Application;
}) => {
  if (application.status !== ApplicationStatus.Submitted) return null;

  return (
    <div className="space-y-3">
      {application.applicants.map((applicant) => {
        const documentRequests = applicant.document_requests || [];
        const pendingDocRequests = documentRequests.filter(
          (dr) => dr.is_uploaded === false
        );
        const notSeenRequests = documentRequests.filter(
          (dr) => dr.seen_by_partner_user === false
        );

        const applicantName = getFullName({
          firstName: applicant.first_name,
          lastName: applicant.last_name,
          middleName: applicant.middle_name,
          short: true
        });

        if (pendingDocRequests.length)
          return (
            <Banner
              key={applicant.id}
              title="Waiting for additional documents"
              body={`Additional document request was sent to ${applicantName} on ${DateTime.fromISO(pendingDocRequests[0].created_at).toLocaleString(DateTime.DATE_MED)}`}
              icon={Loading03Icon}
            />
          );

        if (notSeenRequests.length)
          return (
            <Banner
              key={applicant.id}
              title="New documents received"
              body={`${applicantName} has sent additional documents that may be relevant to your decision`}
              applicantId={String(applicant.id)}
              applicationId={String(applicant.application_id)}
              icon={NotificationSquareIcon}
            />
          );

        return null;
      })}
    </div>
  );
};
